import { computedFrom, bindable } from 'aurelia-framework';
import { Device } from "../../../models/device";
import { StatusColors } from "../../common/status-colors";
import { applicationLogger } from "../../../logging/logger";
import { SignalCssClasses } from "../../common/signal-css-classes";
import { createDarkText, createLightText } from "../../common/common-functions";
import { BatteryCssClasses } from "../../common/battery-css-classes";
import { Store } from "aurelia-store";
import * as State from "../../../store/state";
import IState = State.IState;
import { Subscription } from "rxjs";

import { AsEnumerable } from "linq-es2015/lib/linq";
import { Api } from "../../../services/api";


export class BaseDevice {

	public device: Device;

	protected store: Store<IState>;
	protected api: Api;
	protected applicationState: IState;
	protected subscription: Subscription;
	protected deviceSubscription: Subscription;

	@bindable showWarnings: boolean = false;

	constructor(store: Store<IState>, api: Api) {

		this.store = store;
		this.api = api;

	}

	protected  isDefined<T>(value: T | null | undefined): value is T {
		return value !== undefined && value !== null;
	}


	@computedFrom('device.properties')
	get signalIcon(): string {

		if (!this.device?.data?.rssi || !this.device?.properties?.rssi) {
			return null;
		}

		const rssi = +this.device.data.rssi;
		const levels = this.device.properties.rssi.value.split(",");
		const level0 = +levels[0];
		const level1 = +levels[1];
		const level2 = +levels[2];
		const level3 = +levels[3];

		switch (true) {
			case (rssi > level3):
				return SignalCssClasses.mdiWifiStrength4;
			case (rssi > level2):
				return SignalCssClasses.mdiWifiStrength3;
			case (rssi > level1):
				return SignalCssClasses.mdiWifiStrength2;
			case (rssi > level0):
				return SignalCssClasses.mdiWifiStrength1;
			default:
				return SignalCssClasses.mdiWifiStrengthAlertOutline;
		}
	}
	
	@computedFrom('device.styles')
	get signalColor(): string {
		if (!this.device?.styles?.rssi) {
			return null;
		}
		if (this.device.styles.rssi === StatusColors.yellow) {
			return createDarkText(this.device.styles.rssi);
		}
		return createLightText(this.device.styles.rssi);
	}

	@computedFrom('device.data')
	get signal(): string {
		if (!this.device?.data?.rssi ) {
			return null;
		}
		return `${this.device.data.rssi} dBm`;
	}

	@computedFrom('device.styles')
	get batteryIcon(): string {
		if (!this.device?.styles?.battery) {
			return null;
		}


		if (this.device.data.battery > 0) {
			const batteryLevel = this.calculateBatteryLevel();
			const nearestLevel = Math.ceil(+batteryLevel / 10) * 10;
			return nearestLevel === 100
				? BatteryCssClasses.mdiBattery
				: nearestLevel === 0
				? BatteryCssClasses.mdiBatteryAlert
				: `${BatteryCssClasses.mdiBatteryPrefix}${nearestLevel}`;
		} else {
			return BatteryCssClasses.mdiPluggedIn;
		}
	}

	@computedFrom('device.styles')
	get batteryColor(): string {
		if (!this.device?.styles?.battery) {
			return null;
		}
		if (this.device.styles.battery === StatusColors.yellow) {
			return createDarkText(this.device.styles.battery);
		}
		return createLightText(this.device.styles.battery);
	}

	@computedFrom('device.data')
	get batteryLevel(): string {
		if (!this.device?.data?.battery || this.device?.properties?.length === 0) {
			return null;
		}
		if (this.device.data.battery > 0) {
			return `${this.calculateBatteryLevel()}%`;
		}
		return "";
	}

	calculateBatteryLevel(): string {
		if (!this.device?.data?.battery || !this.device?.properties?.nominalVoltage?.value) {
			return null;
		}
		//if (this.device.data.batteryPercentage)
		const level = (this.device.data.batteryPercentage) ? this.device.data.batteryPercentage : (this.device.data.battery / +this.device.properties.nominalVoltage.value) * 100;
		return (level >= 100 ? 100 : level).toFixed(0);
	}

	@computedFrom('device.data')
	get deviceHasData() {
		return !this.device?.data;
	}

	@computedFrom('device.properties')
	get deviceHasProperties(): boolean {
		return !this.device?.properties;
	}


	unsubscribe() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
		if (this.deviceSubscription) {
			this.deviceSubscription.unsubscribe();
		}
	}

	getDevice(): Device {

		const deviceMetadata = this.device.metadata;
		if (deviceMetadata) {
			const location = AsEnumerable(this.applicationState.locationState.selectedLocations)
				.FirstOrDefault(location => location.id === deviceMetadata.locationId);

			if (location == null) {
				return null;
			}

			const zone = AsEnumerable(location.zones).FirstOrDefault(zone => zone.id === deviceMetadata.zoneId);
			if (zone == null) {
				return null;
			}

			const structure = AsEnumerable(zone.structures)
				.FirstOrDefault(structure => structure.id === deviceMetadata.structureId);
			if (structure == null) {
				return null;
			}
			const devices = AsEnumerable(structure.devices);
			return devices.FirstOrDefault((device: Device) => device.deviceId === deviceMetadata.deviceId);
		}
		return null;
	}

}

